import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from './services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { UserSystemComponent } from './shared/user-system/user-system.component';
import { CommonService } from './services/common.service';
import { IdleService } from './services/idle.service';
import { AppContext } from './models/appContext';
import { GlobalService } from './services/global.service';
import { UserPreferenceComponent } from './shared/user-preferences/user-preferences.component';
import { PermissionComponent } from './shared/permission/permission.component';
import { ContactComponent } from './shared/Contacts/contact.component';
import { RoleEvaluatorService } from './services/role-evaluator.service';
import { UserSubstitutionComponent } from './shared/user-substitution/user-substitution.component';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  currentYear: number = new Date().getFullYear();
  isUserInfoLoaded = false;
  tabs: { title: string; route: string }[] = [];
  selectedTabIndex = 0;
  title = 'Next Gen Digital Imaging';
  userFullName: string = '';
  public isAuthenticated$!: Observable<boolean>;
  footerMessage: string = '';
  messageType: string = '';
  selectedSystemId: string | null = null;
  showWarning = false;
  idleMinutes = 55; // actual idle time
  timeoutMinutes = 5; // warning timeout duration
  countdown = 300; // total seconds remaining (example)
  progressBarValue = 0;
  selectedSystemName: string = '';
  loading: boolean = true;
  userGuideUrl: string = "";
  private readonly intervalId: any;
  constructor(
    private readonly router: Router,
    private readonly _oktaStateService: OktaAuthStateService,
    private readonly  authService: AuthService,
    public dialog: MatDialog,
    private readonly commonService: CommonService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly idleService: IdleService,
    private readonly globalVariable: GlobalService,
    private readonly roleEvaluator: RoleEvaluatorService
  ) {}

  public ngOnInit(): void {
    this._oktaStateService.authState$.subscribe(async authState => {
      if (authState.isAuthenticated) {
        await this.authService.saveUserInfo();
        this.loadUserSystemComponent();
      }
      const visibleTabs = this.roleEvaluator.getVisibleTabs();
      const restrictedPaymentCodeForSystems = ['6001'];
      const systemId: string = this.selectedSystemId || '';

      this.tabs = [
        { title: 'My Work Basket', route: '/home' },
        { title: 'Workflow', route: '/workflow' },
        { title: 'Retrieval', route: '/retrieval' },
        { title: 'Web Upload', route: '/webupload' },
        { title: 'Vendor Classification', route: '/vendorinfo' },
        { title: 'Payment Code', route: '/paymentcode', hideForSystems: restrictedPaymentCodeForSystems },
        { title: 'Substitution', route: '/substitution' }
      ].filter(tab =>
        visibleTabs.includes(tab.title) &&
        !(tab.hideForSystems?.includes(systemId))
      );
    });

    this.idleService.startWatching(() => this.signOut());

    this.idleService.countdown$.subscribe(seconds => {
      this.countdown = seconds;
      this.updateProgressBar();
    });

    this.idleService.isWarningActive$.subscribe(show => {
      this.showWarning = show;
      this.cdRef.detectChanges();
    });

    this.commonService.currentMessage$.subscribe(message => {
      Promise.resolve().then(() => {
        this.footerMessage = message;
        this.cdRef.detectChanges();
      });
    });

    this.commonService.currentType$.subscribe(type => {
      Promise.resolve().then(() => {
        if (type === 'success') {
          this.messageType = 'success-message';
        } else if (type === 'error') {
          this.messageType = 'error-message';
        } else {
          this.messageType = '';
        }
        this.cdRef.detectChanges();
      });
    });
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  loadUserSystemComponent() {
    this.selectedSystemId = this.globalVariable.getSelectedSystemId;
    if (!this.selectedSystemId || this.selectedSystemId === '') {
      this.openUserSystemDialog();
    } else {
      this.selectedSystemName = localStorage.getItem("SelectedSystemName") ?? "";
      this.callRegisterPrincipal(parseInt(this.selectedSystemId));
    }
  }

  formatCountdown(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${this.padZero(mins)}:${this.padZero(secs)}`;
  }

  padZero(value: number): string {
    return value < 10 ? '0' + value : value.toString();
  }

  updateProgressBar() {
    // Calculate progress based on remaining time
    this.progressBarValue = (this.countdown / (this.timeoutMinutes * 60)) * 100;
  }

  isActive(): boolean {
    const activeRoutes = ['home', 'workflow', 'retrieval', 'webupload', 'vendorinfo','paymentcode','substitution' ];
    return activeRoutes.some(route => this.router.url === `/${route}`);
  }

  closeFooterMessage() {
    this.commonService.clearMessage();
  }

  openUserSystemDialog(): void {
    const dialogRef = this.dialog.open(UserSystemComponent, {
      width: '600px',
      height: '134px',
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result === 'ok') {
        location.reload();
      }
    });
  }

  popPermission() {
    const dialogRef = this.dialog.open(PermissionComponent, {
      width: '48%',
      height: '73%',
       maxWidth: '100%'
    });

    dialogRef.afterClosed().subscribe();
  }

  handleRegisterPrincipal() {
    if (this.globalVariable.getSelectedSystemId) {
      this.callRegisterPrincipal(Number(this.globalVariable.getSelectedSystemId));
    }
  }

  callRegisterPrincipal(systemId: number): void {
    this.commonService.callRegisterPrincipal(systemId).subscribe({
      next: (response) => {
        AppContext.userDetails = response;
        AppContext.userDetails.systemId = systemId;
        this.userFullName = response.fullName;
        this.globalVariable.setUserDetails = AppContext.userDetails;
        this.globalVariable.setUserEmail = response.emailId;
        this.getUserGuideUrl();
        this.globalVariable.configureUserAccess(AppContext.userDetails);
      },
      error: (error) => {
        this.commonService.openErrorDialog(error.error.message);
        this.commonService.showMessage('User registration failed', 'error');
      }
    });
  }

  openUserPreferenceDialog(): void {
    this.dialog.open(UserPreferenceComponent, {
      width: '600px',
      height: '700px',
      maxWidth: '100%',
    });
  }

  openUserSubstitutionDialog(): void {
    this.dialog.open(UserSubstitutionComponent, {
      width: '400px',
      height: '525px',
      maxWidth: '100%',
    });
  }
  
  openContact(): void {
    this.dialog.open(ContactComponent, {
      width: '500px',
      height: '220px'
    });
  }

  getUserGuideUrl(): void {
    this.commonService.getUserGuide().subscribe({
      next: (data) => {
        this.userGuideUrl = data[1].trim();
      },
      error: (error) => {
        this.userGuideUrl = '';
      }
    });
  }
  public async signIn(): Promise<void> {
    this.authService.signIn();
  }

  public signOut(): void {
    this.authService.signOut();
  }

  onKeyDown(event: KeyboardEvent): void {
  }
}
